// Story-based mock data that matches the narrative timelines
// Each story has day-by-day data that matches the story progression

export type StoryId = 'jon' | 'ben' | 'jane' | 'henry' | 'sarah' | 'marcus';

export interface StoryDayData {
  day: number;
  vivaScore: number;
  pillars: {
    p1: { score: number; status: string; name: string }; // Heart & BP
    p2: { score: number; status: string; name: string }; // Sleep & Recovery
    p3: { score: number; status: string; name: string }; // Activity & Movement
    p4: { score: number; status: string; name: string }; // Metabolic & Weight
    p5: { score: number; status: string; name: string }; // Mental Wellbeing
  };
  observations: {
    bloodPressureRisk?: { value: number; rangeLevel: string; systolic?: number; diastolic?: number };
    heartRate?: { value: number; rangeLevel: string };
    restingHeartRate?: { value: number; rangeLevel: string };
    stressLevel?: { value: number; rangeLevel: string };
    sleepDuration?: { value: number; rangeLevel: string }; // minutes
    steps?: { value: number; rangeLevel: string };
    vo2Max?: { value: number; rangeLevel: string };
    bmi?: { value: number; rangeLevel: string };
    weight?: { value: number; unit: string };
    diabetesRisk?: { value: string | number; rangeLevel: string };
  };
  badges?: string[];
  points?: number;
  heyVivaExpression?: string;
  heyVivaMessage?: string;
  missions?: string[];
  gameUnlocks?: string[];
  prizes?: Array<{
    type: 'game' | 'reward' | 'feature';
    name: string;
    description?: string;
    icon?: string;
    expiresAt?: Date; // For time-limited prizes
    badgeSource?: string; // Badge that unlocked this prize
  }>;
  // Group-specific metadata for enhanced scenarios
  groupMetadata?: {
    confidenceLevels?: { [key: string]: number }; // p1, p2, etc. -> confidence percentage
    questionnaireExpiryDate?: Date | null; // For mental wellbeing (p5)
    hasZeroData?: string[]; // List of group IDs with zero data (should be hidden)
  };
}

// Story boundaries
export const STORY_BOUNDARIES: Record<StoryId, { minDay: number; maxDay: number }> = {
  'ben': { minDay: 1, maxDay: 90 },
  'jon': { minDay: 1, maxDay: 270 },
  'jane': { minDay: 1, maxDay: 270 },
  'henry': { minDay: 0, maxDay: 280 },
  'sarah': { minDay: 1, maxDay: 180 },
  'marcus': { minDay: 1, maxDay: 240 }
};

// Helper function to get data for a specific day (interpolates between key days)
export function getStoryDataForDay(storyId: StoryId, day: number): StoryDayData | null {
  const storyData = STORY_DATA[storyId];
  if (!storyData) return null;

  // Validate day is within story boundaries
  const boundaries = STORY_BOUNDARIES[storyId];
  if (day < boundaries.minDay || day > boundaries.maxDay) {
    return null;
  }

  // Find the closest day data (exact match or previous)
  let selectedDay = day;
  let dayData = storyData[selectedDay];
  
  // If exact day not found, find the previous key day
  // For Henry, start from 0, for others start from 1
  const minDay = boundaries.minDay;
  while (!dayData && selectedDay >= minDay) {
    selectedDay--;
    dayData = storyData[selectedDay];
  }

  if (!dayData) return null;

  // If we're between key days, interpolate values
  const nextKeyDay = Object.keys(storyData)
    .map(Number)
    .filter(d => d > day)
    .sort((a, b) => a - b)[0];

  if (nextKeyDay && nextKeyDay !== day) {
    const nextData = storyData[nextKeyDay];
    const daysDiff = nextKeyDay - selectedDay;
    const progress = (day - selectedDay) / daysDiff;

    return interpolateData(dayData, nextData, progress);
  }

  return dayData;
}

function interpolateData(
  from: StoryDayData,
  to: StoryDayData,
  progress: number
): StoryDayData {
  return {
    day: Math.round(from.day + (to.day - from.day) * progress),
    vivaScore: Math.round(from.vivaScore + (to.vivaScore - from.vivaScore) * progress),
    pillars: {
      p1: {
        score: Math.round(from.pillars.p1.score + (to.pillars.p1.score - from.pillars.p1.score) * progress),
        status: progress > 0.5 ? to.pillars.p1.status : from.pillars.p1.status,
        name: from.pillars.p1.name
      },
      p2: {
        score: Math.round(from.pillars.p2.score + (to.pillars.p2.score - from.pillars.p2.score) * progress),
        status: progress > 0.5 ? to.pillars.p2.status : from.pillars.p2.status,
        name: from.pillars.p2.name
      },
      p3: {
        score: Math.round(from.pillars.p3.score + (to.pillars.p3.score - from.pillars.p3.score) * progress),
        status: progress > 0.5 ? to.pillars.p3.status : from.pillars.p3.status,
        name: from.pillars.p3.name
      },
      p4: {
        score: Math.round(from.pillars.p4.score + (to.pillars.p4.score - from.pillars.p4.score) * progress),
        status: progress > 0.5 ? to.pillars.p4.status : from.pillars.p4.status,
        name: from.pillars.p4.name
      },
      p5: {
        score: Math.round(from.pillars.p5.score + (to.pillars.p5.score - from.pillars.p5.score) * progress),
        status: progress > 0.5 ? to.pillars.p5.status : from.pillars.p5.status,
        name: from.pillars.p5.name
      }
    },
    observations: {
      ...from.observations,
      bloodPressureRisk: from.observations.bloodPressureRisk ? {
        ...from.observations.bloodPressureRisk,
        value: Math.round((from.observations.bloodPressureRisk.value || 0) + 
          ((to.observations.bloodPressureRisk?.value || 0) - (from.observations.bloodPressureRisk.value || 0)) * progress)
      } : undefined,
      steps: from.observations.steps ? {
        ...from.observations.steps,
        value: Math.round((from.observations.steps.value || 0) + 
          ((to.observations.steps?.value || 0) - (from.observations.steps.value || 0)) * progress)
      } : undefined
    },
    // Badges accumulate - once earned, they stay earned
    badges: [...new Set([...(from.badges || []), ...(to.badges || [])])],
    // Points accumulate
    points: Math.max(from.points || 0, to.points || 0),
    // Use the most recent HeyViva state (closer to next day = use next day's state)
    heyVivaExpression: progress > 0.5 ? (to.heyVivaExpression || from.heyVivaExpression) : from.heyVivaExpression,
    heyVivaMessage: progress > 0.5 ? (to.heyVivaMessage || from.heyVivaMessage) : from.heyVivaMessage,
    // Missions available on the current day (use next day's missions if closer to next day)
    missions: progress > 0.5 ? (to.missions || from.missions || []) : (from.missions || to.missions || []),
    // Game unlocks accumulate
    gameUnlocks: [...new Set([...(from.gameUnlocks || []), ...(to.gameUnlocks || [])])],
    // Prizes accumulate
    prizes: [...(from.prizes || []), ...(to.prizes || [])],
    // Group metadata - use from day's metadata, or interpolate if both have it
    groupMetadata: from.groupMetadata || to.groupMetadata || undefined
  };
}

// Story data organized by day
const STORY_DATA: Record<StoryId, Record<number, StoryDayData>> = {
  jon: {
    1: {
      day: 1,
      vivaScore: 62,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 4368, rangeLevel: 'Fair' },
        restingHeartRate: { value: 59, rangeLevel: 'Excellent' },
        sleepDuration: { value: 375, rangeLevel: 'Fair' }, // 6h 15m
        vo2Max: { value: 30.8, rangeLevel: 'Fair' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 85, // High confidence - has scan data
          p2: 40, // Low confidence - missing sleep stages
          p3: 60, // Medium confidence - has steps but missing VO2Max
          p4: 50, // Medium confidence - missing body composition
          p5: 30  // Low confidence - no questionnaire data
        },
        hasZeroData: [] // All groups have some data
      },
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Hi Jon! A 62 is a starting point. Your heart is strong (Resting HR 59!), but we need to get you moving. Tap to see why.",
      missions: ['Complete your first scan', 'Scan Scout: Complete 3 scans this week', 'Complete this questionnaire to activate your Mind & Mood Ring']
    },
    2: {
      day: 2,
      vivaScore: 62,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 65, rangeLevel: 'Average' },
        stressLevel: { value: 75, rangeLevel: 'High' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 90, // Improved after scan
          p2: 45, // Still low - missing sleep stages
          p3: 65, // Improved
          p4: 55,
          p5: 35  // Still low - no questionnaire
        },
        hasZeroData: []
      },
      points: 10,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Scan complete! You've taken the first step.",
      missions: ['Continue daily scans', 'Scan Scout progress: 1/3', 'Log your mood today to boost your Mind & Mood Ring confidence']
    },
    3: {
      day: 3,
      vivaScore: 62,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 55, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 0, status: 'Unknown', name: 'Mental Wellbeing' } // Zero data - should be hidden
      },
      observations: {
        steps: { value: 2100, rangeLevel: 'Poor' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 90,
          p2: 45, // Low confidence
          p3: 35, // Very low confidence - missing wearable
          p4: 55,
          p5: 0   // Zero data - no questionnaire
        },
        hasZeroData: ['p5'] // Mental wellbeing has zero data
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "Don't worry about yesterday. Let's focus on Activity. Ready for a quick quiz?",
      missions: ['Complete a scan to keep your streak alive', 'Take the Activity & Movement quiz', 'Connect your fitness tracker to improve your Activity & Movement Ring confidence']
    },
    4: {
      day: 4,
      vivaScore: 62,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' } // Improved after questionnaire
      },
      observations: {
        steps: { value: 4368, rangeLevel: 'Fair' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 95, // High confidence
          p2: 50, // Medium confidence
          p3: 70, // Good confidence
          p4: 60,
          p5: 80  // High confidence after questionnaire completion
        },
        hasZeroData: [],
        // Questionnaire completed 5 days ago, expires in 25 days
        questionnaireExpiryDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000)
      },
      badges: ['Movement Student (Tier 1)'],
      points: 15,
      gameUnlocks: ['Pacman'],
      prizes: [
        {
          type: 'game',
          name: 'Pacman',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Movement Student'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Nicely done! You're a Movement Student now. Pacman is unlocked as a reward—enjoy a quick break!",
      missions: ['Continue daily scans', 'Log your steps for 3 days']
    },
    7: {
      day: 7,
      vivaScore: 63,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 62, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 4500, rangeLevel: 'Fair' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 100, // Perfect confidence
          p2: 55,  // Improved
          p3: 75,  // Good
          p4: 65,
          p5: 75   // Still good
        },
        hasZeroData: [],
        // Questionnaire expires in 23 days
        questionnaireExpiryDate: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000)
      },
      badges: ['Scan Scout (Tier 1)'],
      points: 25,
      gameUnlocks: ['Snake'],
      prizes: [
        {
          type: 'game',
          name: 'Snake',
          description: 'Classic arcade game unlocked!',
          icon: 'pulse-outline',
          badgeSource: 'Scan Scout'
        }
      ],
      heyVivaExpression: 'Proud',
      heyVivaMessage: "Badge Unlocked: Scan Scout! You've completed 3 scans this week. Snake is unlocked as a reward—enjoy a quick break!",
      missions: ['Maintain your scan streak', 'Weekly Challenge: Hit 7,000 steps on 3 days this week (based on daily summaries)']
    },
    35: {
      day: 35,
      vivaScore: 65,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 0, status: 'Unknown', name: 'Mental Wellbeing' } // Expired questionnaire
      },
      observations: {
        steps: { value: 5500, rangeLevel: 'Fair' },
        sleepDuration: { value: 390, rangeLevel: 'Fair' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 100,
          p2: 60,
          p3: 80,
          p4: 70,
          p5: 0   // Zero confidence - questionnaire expired
        },
        hasZeroData: ['p5'], // Mental wellbeing has expired
        // Questionnaire expired 5 days ago
        questionnaireExpiryDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
      },
      points: 100,
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Your Mind & Mood questionnaire has expired. Complete a new questionnaire to reactivate your Mind & Mood Ring.",
      missions: ['Complete this questionnaire to activate your Mind & Mood Ring', 'Complete a scan today to improve your Heart & Blood Pressure Ring confidence']
    },
    50: {
      day: 50,
      vivaScore: 68,
      pillars: {
        p1: { score: 88, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 72, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7500, rangeLevel: 'Good' },
        sleepDuration: { value: 420, rangeLevel: 'Good' }, // 7 hours
        restingHeartRate: { value: 58, rangeLevel: 'Excellent' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 100, // Perfect - all observations available
          p2: 100, // Perfect - wearable connected
          p3: 100, // Perfect - wearable connected
          p4: 95,  // Very high
          p5: 100  // Perfect - questionnaire completed recently
        },
        hasZeroData: [],
        // Questionnaire expires in 20 days
        questionnaireExpiryDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000)
      },
      points: 200,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Excellent progress! All your groups have high confidence. You're getting a complete picture of your health!",
      missions: ['Maintain your excellent health', 'Continue daily scans']
    },
    60: {
      day: 60,
      vivaScore: 55,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 45, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 55, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 3000, rangeLevel: 'Poor' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 35, // Very low - missing HRV, BP readings
          p2: 25, // Very low - no wearable, missing sleep stages
          p3: 30, // Very low - no wearable connected
          p4: 40, // Low - missing body composition
          p5: 20  // Very low - no questionnaire, no emotion logs
        },
        hasZeroData: [],
        questionnaireExpiryDate: null // No questionnaire data
      },
      points: 50,
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Your groups have low confidence. Connect devices and complete questionnaires to get a more accurate health picture.",
      missions: ['Connect your fitness tracker to improve your Activity & Movement Ring confidence', 'Complete this questionnaire to activate your Mind & Mood Ring', 'Complete a scan today to improve your Heart & Blood Pressure Ring confidence']
    },
    80: {
      day: 80,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 87, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 88, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 12000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8 hours
        restingHeartRate: { value: 55, rangeLevel: 'Excellent' },
        vo2Max: { value: 45, rangeLevel: 'Excellent' }
      },
      groupMetadata: {
        confidenceLevels: {
          p1: 100,
          p2: 100,
          p3: 100,
          p4: 100,
          p5: 100  // All perfect
        },
        hasZeroData: [],
        questionnaireExpiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
      },
      points: 500,
      badges: ['Scan Legend (Tier 5)', 'Step Legend (Tier 5)', 'Sleep Legend (Tier 5)', 'All Groups Excellent (Master)'],
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Outstanding! You've achieved excellence across all groups with perfect confidence. You're a health master!",
      missions: ['Maintain your excellence', 'Continue optimization challenges']
    },
    20: {
      day: 20,
      vivaScore: 65,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 5500, rangeLevel: 'Fair' },
        vo2Max: { value: 31.0, rangeLevel: 'Fair' },
        sleepDuration: { value: 390, rangeLevel: 'Fair' } // 6h 30m
      },
      points: 50,
      heyVivaExpression: 'Excited',
      heyVivaMessage: "Yes! Yesterday's walk pushed you over 7k steps. Two more days to the Step Scout badge.",
      missions: ['Weekly Challenge: Hit 7,000 steps on 3 days this week (1/3, based on daily summaries)', 'Continue daily scans']
    },
    25: {
      day: 25,
      vivaScore: 61,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 315, rangeLevel: 'Poor' }, // 5h 15m
        restingHeartRate: { value: 64, rangeLevel: 'Average' }
      },
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Your score dipped to 61. Yesterday's short sleep (5h 15m) spiked your resting heart rate. Try to get to bed by 10:30 PM tonight.",
      missions: ['Get to bed by 10:30 PM tonight', 'Read article: Sleep and Recovery']
    },
    45: {
      day: 45,
      vivaScore: 68,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 75, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7200, rangeLevel: 'Good' },
        vo2Max: { value: 32.5, rangeLevel: 'Fair' },
        sleepDuration: { value: 405, rangeLevel: 'Fair' } // 6h 45m
      },
      badges: ['Step Guardian (Tier 3)'],
      points: 100,
      gameUnlocks: ['Tetris'],
      prizes: [
        {
          type: 'game',
          name: 'Tetris',
          description: 'Classic puzzle game unlocked!',
          icon: 'grid-outline',
          badgeSource: 'Step Guardian'
        }
      ],
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "You are a Step Guardian! Your consistency is actually improving your VO2Max. Keep this up!",
      missions: ['Maintain 7,000+ steps for 3 weeks', 'Continue daily scans']
    },
    65: {
      day: 65,
      vivaScore: 69,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 75, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8000, rangeLevel: 'Good' },
        vo2Max: { value: 34.0, rangeLevel: 'Fair' },
        sleepDuration: { value: 420, rangeLevel: 'Good' } // 7h
      },
      points: 75,
      heyVivaExpression: 'Coaching',
      heyVivaMessage: "Thanks for logging. To boost your score to 75, we need to focus on intensity, not just yesterday's step count.",
      missions: ['Metabolic Momentum: Log your weight and take a waist measurement today', 'Complete 3 Brisk walks (HR > 110bpm) this week']
    },
    90: {
      day: 90,
      vivaScore: 68,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 3200, rangeLevel: 'Poor' },
        stressLevel: { value: 75, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "I see your stress is high. Yesterday's steps were lower, but that's okay. Can you do a 2-minute breathing exercise?",
      missions: ['Complete a scan to refresh your HealthScore', 'Do a 2-minute breathing exercise']
    },
    110: {
      day: 110,
      vivaScore: 78,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        vo2Max: { value: 36.5, rangeLevel: 'Good' },
        restingHeartRate: { value: 55, rangeLevel: 'Excellent' },
        sleepDuration: { value: 435, rangeLevel: 'Good' }, // 7h 15m
        steps: { value: 9500, rangeLevel: 'Excellent' }
      },
      badges: ['Metabolic Explorer'],
      points: 150,
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "78! You are knocking on the door of 80. Your VO2Max improvement is the main driver.",
      missions: ['Maintain 9,500+ steps', 'Continue weekend hiking']
    },
    150: {
      day: 150,
      vivaScore: 81,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 75, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 80, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' }, // 7h 30m
        vo2Max: { value: 38.0, rangeLevel: 'Good' }
      },
      points: 200,
      heyVivaExpression: 'Proud',
      heyVivaMessage: "Congratulations! You've reached High Performer status (81). Elite Challenges are now unlocked!",
      missions: ['Perfect Week: Keep all pillars in Good or Excellent for 7 days', 'Elite Challenge: Beat your Personal Best steps']
    },
    175: {
      day: 175,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 90, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 85, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        vo2Max: { value: 41.0, rangeLevel: 'Excellent' },
        steps: { value: 10500, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      badges: ['Excellence Maintenance (Tier 1)', 'Master Badge'],
      points: 300,
      gameUnlocks: ['All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games',
          description: 'All arcade games permanently unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Master Badge'
        },
        {
          type: 'reward',
          name: 'Amazon £10 Voucher',
          description: '£10 Amazon gift voucher!',
          icon: 'gift-outline',
          badgeSource: 'Master Badge'
        },
        {
          type: 'feature',
          name: 'Scan a Friend',
          description: 'Unlock the ability to scan friends and family!',
          icon: 'people-outline',
          badgeSource: 'Master Badge'
        }
      ],
      heyVivaExpression: 'Star-Struck',
      heyVivaMessage: "LEGENDARY! You hit 85! You've transformed your health profile over 6 months.",
      missions: ['Maintain Elite status (85+)', 'Continue Perfect Week challenges']
    },
    200: {
      day: 200,
      vivaScore: 82,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 85, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 6000, rangeLevel: 'Fair' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      heyVivaExpression: 'Coaching',
      heyVivaMessage: "You're still in the Elite zone (82), but yesterday's activity was lower. Challenge: Beat your Personal Best steps (12k) this Saturday.",
      missions: ['Elite Challenge: Beat your Personal Best steps (12k) this Saturday', 'Maintain Elite status']
    },
    230: {
      day: 230,
      vivaScore: 76,
      pillars: {
        p1: { score: 85, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 65, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 75, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 300, rangeLevel: 'Poor' }, // 5h
        restingHeartRate: { value: 65, rangeLevel: 'Average' },
        steps: { value: 10000, rangeLevel: 'Excellent' }
      },
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "Welcome back! Vacation took a little toll (Score: 76). Let's do a 'Recovery Week'. Focus on Sleep first.",
      missions: ['Recovery Week: Sleep Scout Redux - 3 nights of 7h+ sleep', 'Focus on sleep recovery']
    },
    270: {
      day: 270,
      vivaScore: 83,
      pillars: {
        p1: { score: 88, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 82, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 435, rangeLevel: 'Good' }, // 7h 15m
        vo2Max: { value: 40.5, rangeLevel: 'Excellent' }
      },
      badges: ['Health Master (6 Months Consistency)', 'Excellence Maintenance (Tier 1)'],
      points: 400,
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "You've mastered the system, Jon. It's not about the score anymore, it's about how you feel. (But the 83 looks great too!)",
      missions: ['Maintain Elite status', 'Continue sustainable lifestyle habits']
    }
  },
  ben: {
    1: {
      day: 1,
      vivaScore: 45,
      pillars: {
        p1: { score: 40, status: 'Poor', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 45, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 40, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 25, rangeLevel: 'Poor', systolic: 160, diastolic: 98 },
        diabetesRisk: { value: 'High', rangeLevel: 'High' },
        bmi: { value: 35, rangeLevel: 'Poor' },
        steps: { value: 800, rangeLevel: 'Poor' }
      },
      heyVivaExpression: 'Concerned',
      heyVivaMessage: "Hi Ben. A score of 45 means we need to pay attention right now. Your Blood Pressure is the main driver. We can fix this together, step by step.",
      missions: ['Complete your first scan', 'Learn about Blood Pressure basics']
    },
    3: {
      day: 3,
      vivaScore: 45,
      pillars: {
        p1: { score: 40, status: 'Poor', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 45, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 40, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 25, rangeLevel: 'Poor', systolic: 160, diastolic: 98 },
        steps: { value: 800, rangeLevel: 'Poor' }
      },
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Ben, let's continue tracking your progress. Complete a scan to check your Blood Pressure status.",
      missions: ['Complete a scan to check your Blood Pressure', 'Learn about Blood Pressure basics']
    },
    4: {
      day: 4,
      vivaScore: 46,
      pillars: {
        p1: { score: 42, status: 'Poor', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 46, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 40, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 30, rangeLevel: 'Poor', systolic: 158, diastolic: 95 },
        steps: { value: 1200, rangeLevel: 'Poor' }
      },
      badges: ['Scan Scout (Tier 1)'],
      points: 25,
      gameUnlocks: ['Pacman'],
      prizes: [
        {
          type: 'game',
          name: 'Pacman',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Scan Scout'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "You came back! Scan complete. Pacman is unlocked as a reward—enjoy a quick entertainment break!",
      missions: ['Take the Blood Pressure Basics Quiz', 'Log your water intake (3 glasses today)']
    },
    7: {
      day: 7,
      vivaScore: 46,
      pillars: {
        p1: { score: 42, status: 'Poor', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 46, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 41, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 28, rangeLevel: 'Poor', systolic: 158, diastolic: 95 },
        steps: { value: 1200, rangeLevel: 'Poor' }
      },
      points: 75,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Great job on the quiz! You learned that walking lowers BP. Your game reward is available—enjoy a quick break!",
      missions: ['Log 3 glasses of water today', 'Take a 10-minute walk']
    },
    15: {
      day: 15,
      vivaScore: 48,
      pillars: {
        p1: { score: 45, status: 'Poor', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 48, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 42, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 40, rangeLevel: 'Fair', systolic: 150, diastolic: 90 },
        steps: { value: 1500, rangeLevel: 'Poor' }
      },
      points: 50,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Hydration helps flush out sugar and lower pressure. Good job.",
      missions: ['Log your water intake (3/3 glasses)', 'Continue daily scans']
    },
    25: {
      day: 25,
      vivaScore: 50,
      pillars: {
        p1: { score: 50, status: 'Fair', name: 'Heart & Blood Pressure' },
        p2: { score: 52, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 52, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 45, status: 'Poor', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 2500, rangeLevel: 'Fair' },
        bloodPressureRisk: { value: 45, rangeLevel: 'Fair', systolic: 150, diastolic: 90 }
      },
      badges: ['Movement Scout (Tier 1)'],
      points: 50,
      gameUnlocks: ['Snake'],
      prizes: [
        {
          type: 'game',
          name: 'Snake',
          description: 'Classic arcade game unlocked!',
          icon: 'pulse-outline',
          badgeSource: 'Movement Scout'
        }
      ],
      heyVivaMessage: "You broke 50! Yesterday's walk helped. Your BP risk dropped slightly to 'Fair'.",
      missions: ['Maintain 2,500+ steps for 3 days', 'Continue daily scans']
    },
    40: {
      day: 40,
      vivaScore: 55,
      pillars: {
        p1: { score: 55, status: 'Fair', name: 'Heart & Blood Pressure' },
        p2: { score: 55, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 58, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 50, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 52, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 55, rangeLevel: 'Fair', systolic: 140, diastolic: 85 },
        weight: { value: -2, unit: 'kg' },
        steps: { value: 3000, rangeLevel: 'Fair' }
      },
      badges: ['BP Guardian (Tier 1)'],
      points: 100,
      prizes: [
        {
          type: 'reward',
          name: 'Costa Coffee',
          description: 'Free coffee from Costa!',
          icon: 'cafe-outline',
          badgeSource: 'BP Guardian'
        }
      ],
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "Look at this chart, Ben. Your Blood Pressure is down 20 points since Day 1. You are actively lowering your stroke risk.",
      missions: ['Reach 4,000 steps for 3 days', 'Maintain BP in Fair range']
    },
    55: {
      day: 55,
      vivaScore: 58,
      pillars: {
        p1: { score: 62, status: 'Average', name: 'Heart & Blood Pressure' },
        p2: { score: 58, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 62, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 52, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 58, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 62, rangeLevel: 'Average', systolic: 135, diastolic: 82 },
        steps: { value: 4000, rangeLevel: 'Average' }
      },
      points: 150,
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "You're so close! Just 2 more points to reach the Safe Zone. Keep up those 4,000 steps!",
      missions: ['The Safe Zone Challenge: 3 days of Green scans (BP < 135/85) or 3 days of 4,000 steps']
    },
    60: {
      day: 60,
      vivaScore: 61,
      pillars: {
        p1: { score: 65, status: 'Average', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 55, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 65, rangeLevel: 'Average', systolic: 135, diastolic: 82 },
        diabetesRisk: { value: 'Moderate', rangeLevel: 'Moderate' },
        steps: { value: 4100, rangeLevel: 'Average' }
      },
      badges: ['Health Hero (Tier 1)'],
      points: 200,
      gameUnlocks: ['All Games (72h)'],
      heyVivaExpression: 'Legend',
      heyVivaMessage: "You did it. You reached the Safe Zone (61). You walked away from the 'Danger Zone'. Literally.",
      missions: ['Maintain Safe Zone for 7 days', 'Keep BP below 135/85']
    },
    70: {
      day: 70,
      vivaScore: 59,
      pillars: {
        p1: { score: 62, status: 'Average', name: 'Heart & Blood Pressure' },
        p2: { score: 58, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 55, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 58, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 1000, rangeLevel: 'Poor' },
        bloodPressureRisk: { value: 60, rangeLevel: 'Fair', systolic: 138, diastolic: 88 }
      },
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Maintenance is harder than improvement. Yesterday's steps were below 4,000. Let's aim for 4,000+ today.",
      missions: ['Aim for 4,000+ steps today (will show tomorrow)', 'Complete a scan to check your status']
    },
    80: {
      day: 80,
      vivaScore: 60,
      pillars: {
        p1: { score: 64, status: 'Average', name: 'Heart & Blood Pressure' },
        p2: { score: 59, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 63, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 56, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 64, rangeLevel: 'Average', systolic: 133, diastolic: 81 },
        steps: { value: 3500, rangeLevel: 'Fair' },
        weight: { value: -4, unit: 'kg' }
      },
      badges: ['Metabolic Scout (Tier 1)'],
      points: 75,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Sugar Swap complete! Replacing soda with water is helping your metabolism. Weight is trending down.",
      missions: ['Sugar Swap: Replace one soda with water today', 'Maintain 4,000 steps']
    },
    90: {
      day: 90,
      vivaScore: 62,
      pillars: {
        p1: { score: 65, status: 'Average', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 58, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 62, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 68, rangeLevel: 'Average', systolic: 132, diastolic: 80 },
        steps: { value: 4000, rangeLevel: 'Average' },
        weight: { value: -5, unit: 'kg' }
      },
      heyVivaMessage: "90 days ago, we were in the red. Today, you're in control. Let's keep going."
    }
  },
  jane: {
    1: {
      day: 1,
      vivaScore: 80,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Unknown', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 0, rangeLevel: 'Unknown' }
      },
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Welcome, Jane! An 80 is a fantastic start based on your scan. Your heart health is excellent. I'm syncing with Apple Health now to build your full profile.",
      missions: ['Complete your first scan', 'Check Apple Health permissions']
    },
    3: {
      day: 3,
      vivaScore: 80,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Unknown', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 0, rangeLevel: 'Unknown' }
      },
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "Jane, I have permission to read your Apple Health data, but I'm seeing 0 steps. Is your phone tracking you, or is the data stuck?",
      missions: ['Troubleshoot Data: Check if Motion & Fitness is enabled in iPhone Privacy', 'Open Apple Health to verify steps exist']
    },
    4: {
      day: 4,
      vivaScore: 78,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Unknown', name: 'Sleep & Recovery' },
        p3: { score: 55, status: 'Poor', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 2100, rangeLevel: 'Poor' }
      },
      points: 10,
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "Okay, data is flowing! I see 2,100 steps. It's a start, but it pulled your score to 78. Are you doing workouts I can't see?",
      missions: ['Log your workouts manually', 'Continue daily scans']
    },
    5: {
      day: 5,
      vivaScore: 79,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Unknown', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Unknown', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 2500, rangeLevel: 'Fair' }
      },
      badges: ['Mood Scout (Tier 1)'],
      points: 25,
      gameUnlocks: ['Sudoku'],
      prizes: [
        {
          type: 'game',
          name: 'Sudoku',
          description: 'Puzzle game unlocked!',
          icon: 'apps-outline',
          badgeSource: 'Mood Scout'
        },
        {
          type: 'reward',
          name: 'Costa Coffee',
          description: 'Free coffee from Costa!',
          icon: 'cafe-outline',
          badgeSource: 'Mood Scout'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Yoga logged! +60 active minutes. This helps, but since I can't see your Heart Rate, I'm estimating the calorie burn conservatively.",
      missions: ['Continue logging Yoga sessions', 'Consider wearing a watch during workouts for better credit']
    },
    20: {
      day: 20,
      vivaScore: 79,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 2500, rangeLevel: 'Fair' }
      },
      points: 50,
      heyVivaExpression: 'Coaching',
      heyVivaMessage: "Jane, you're doing the work, but your score is 79. Manual logs don't give you credit for Intensity (Heart Rate). A wearable would likely push you over 80 instantly.",
      missions: ['Consider wearing a watch during workouts for better credit', 'Continue logging Yoga sessions']
    },
    25: {
      day: 25,
      vivaScore: 84,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8000, rangeLevel: 'Excellent' },
        heartRate: { value: 115, rangeLevel: 'Good' }
      },
      badges: ['Movement Explorer (Tier 2)'],
      points: 75,
      gameUnlocks: ['All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games',
          description: 'All arcade games unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Movement Explorer'
        },
        {
          type: 'reward',
          name: 'Curve Movie Ticket',
          description: 'Free movie ticket from Curve!',
          icon: 'film-outline',
          badgeSource: 'Movement Explorer'
        }
      ],
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "There it is! Your active heart rate confirms your fitness level. You've jumped to 84!",
      missions: ['Maintain watch wearing during workouts', 'Continue daily scans']
    },
    40: {
      day: 40,
      vivaScore: 84,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8000, rangeLevel: 'Excellent' }
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "Good morning, Jane! Since you don't wear your watch to bed, your Morning Scan is the only way I can judge your recovery.",
      missions: ['Recovery Ritual: Scan within 30 mins of waking up for 5 days', 'Continue watch wearing during workouts']
    },
    45: {
      day: 45,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 82, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 78, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        restingHeartRate: { value: 52, rangeLevel: 'Excellent' }
      },
      badges: ['Scan Explorer (7-day streak)'],
      points: 100,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Your high HRV tells me you slept well, even without the tracking data. Keeping P2 in the Green!",
      missions: ['Maintain morning scan routine', 'Continue watch wearing during workouts']
    },
    70: {
      day: 70,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 82, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 80, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        restingHeartRate: { value: 52, rangeLevel: 'Excellent' },
        stressLevel: { value: 70, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Concerned',
      heyVivaMessage: "Your recovery is lower based on yesterday's data. Your Yoga usually helps this. Can you fit in a 15-min session?",
      missions: ['Do a 20-min Yoga session', 'Wear watch during Yoga for better tracking']
    },
    90: {
      day: 90,
      vivaScore: 87,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 85, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        vo2Max: { value: 40, rangeLevel: 'Good' },
        restingHeartRate: { value: 50, rangeLevel: 'Excellent' }
      },
      badges: ['Consistency Queen (Elite Tier)'],
      points: 200,
      heyVivaExpression: 'Proud',
      heyVivaMessage: "You are in the top 5% of users. To reach 90, we need to push your VO2Max slightly higher. Yoga is great, but a little cardio would tip the scale.",
      missions: ['Heart Pumper: 2 sessions of Brisk pace walking or flow (HR > 120) this week', 'Maintain Elite status']
    },
    120: {
      day: 120,
      vivaScore: 89,
      pillars: {
        p1: { score: 92, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 90, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 88, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 90, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        vo2Max: { value: 42, rangeLevel: 'Excellent' },
        restingHeartRate: { value: 48, rangeLevel: 'Excellent' }
      },
      badges: ['Perfect Week (Tier 1)'],
      points: 250,
      gameUnlocks: ['All Games'],
      heyVivaExpression: 'Legend',
      heyVivaMessage: "PERFECT WEEK. All pillars are Green. This is what mastery looks like.",
      missions: ['Maintain Perfect Week status', 'Continue power walking with Yoga']
    },
    145: {
      day: 145,
      vivaScore: 90,
      pillars: {
        p1: { score: 95, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 92, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 90, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 92, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 90, rangeLevel: 'Excellent' },
        vo2Max: { value: 42, rangeLevel: 'Excellent' }
      },
      badges: ['Yoga Master'],
      points: 300,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Welcome to the 90 Club, Jane! You did this your way—Yoga, mindful mornings, and listening to your body.",
      missions: ['Maintain 90+ score', 'Continue morning scan routine']
    },
    146: {
      day: 146,
      vivaScore: 90,
      pillars: {
        p1: { score: 95, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 92, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 90, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 92, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        bloodPressureRisk: { value: 90, rangeLevel: 'Excellent' },
        vo2Max: { value: 42, rangeLevel: 'Excellent' }
      },
      badges: ['Yoga Master'],
      points: 300,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Welcome to the 90 Club, Jane! You did this your way—Yoga, mindful mornings, and listening to your body.",
      missions: ['Maintain 90+ score', 'Continue morning scan routine']
    },
    200: {
      day: 200,
      vivaScore: 90,
      pillars: {
        p1: { score: 95, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 92, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 90, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 92, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 0, rangeLevel: 'Unknown' }
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "No watch? No problem. You've earned enough 'Trust Credit' to keep your score stable for 48 hours. Just keep scanning in the morning.",
      missions: ['Continue morning scans', 'Maintain Elite status']
    },
    270: {
      day: 270,
      vivaScore: 90,
      pillars: {
        p1: { score: 95, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 92, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 90, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 92, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Excellent' },
        restingHeartRate: { value: 48, rangeLevel: 'Excellent' },
        vo2Max: { value: 42, rangeLevel: 'Excellent' }
      },
      badges: ['Scan Legend (Tier 5)', 'Calm Champion (Tier 4)', 'Movement Guardian (Tier 3)', 'Yoga Master'],
      points: 500,
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "9 months of mastery, Jane. You've proven that health tracking can work on your terms—Yoga, mindful mornings, and listening to your body.",
      missions: ['Maintain Elite status', 'Continue sustainable lifestyle habits']
    }
  },
  henry: {
    0: {
      day: 0,
      vivaScore: 74,
      pillars: {
        p1: { score: 80, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 72, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        stressLevel: { value: 80, rangeLevel: 'High' },
        sleepDuration: { value: 330, rangeLevel: 'Poor' }, // <6h
        steps: { value: 4000, rangeLevel: 'Fair' }
      },
      heyVivaExpression: 'Concerned',
      heyVivaMessage: "I see the stress spike. Grinding for badges right now isn't the answer. Let's try a 2-Minute Reset.",
      missions: ['Log your mood and take a breath', "Focus on getting a good night's sleep"]
    },
    1: {
      day: 1,
      vivaScore: 74,
      pillars: {
        p1: { score: 80, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 60, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 72, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        stressLevel: { value: 80, rangeLevel: 'High' }
      },
      points: 10,
      gameUnlocks: ['Pacman'],
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "Welcome back, Henry. Pacman is unlocked as a reward—enjoy a quick break. Focus on recovery today.",
      missions: ['Log your mood and take a breath', "Focus on getting a good night's sleep"]
    },
    3: {
      day: 3,
      vivaScore: 75,
      pillars: {
        p1: { score: 80, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 65, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 72, status: 'Fair', name: 'Metabolic & Weight' },
        p5: { score: 55, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 430, rangeLevel: 'Good' } // 7h 10m
      },
      badges: ['Sleep Savior (Tier 1)'],
      points: 25,
      gameUnlocks: ['Space Invaders'],
      prizes: [
        {
          type: 'game',
          name: 'Space Invaders',
          description: 'Classic arcade game unlocked!',
          icon: 'rocket-outline',
          badgeSource: 'Sleep Savior'
        }
      ],
      heyVivaExpression: 'Thinking',
      heyVivaMessage: "I've hidden a 'Deep Sleep' Mystery Badge. It only unlocks if you get 7+ hours tonight. It comes with a game reward for a quick break.",
      missions: ['Get 7+ hours of sleep tonight', 'Continue recovery focus']
    },
    7: {
      day: 7,
      vivaScore: 78,
      pillars: {
        p1: { score: 82, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 70, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 75, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 75, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' }
      },
      points: 50,
      heyVivaExpression: 'Legend',
      heyVivaMessage: "Comeback complete. You recognized the burnout and fixed it. Score is 78.",
      missions: ['Continue recovery focus', 'Weekend Stress Buster: Complete walking challenge']
    },
    15: {
      day: 15,
      vivaScore: 79,
      pillars: {
        p1: { score: 82, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 76, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 76, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 62, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' },
        heartRate: { value: 115, rangeLevel: 'Good' }
      },
      badges: ['Heart Health Student (Tier 1)'],
      points: 75,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Zone 2 Hunter complete! It's not about how far you walk, but the pace. Keep HR between 110-120bpm for 20 mins.",
      missions: ['Zone 2 Hunter: Keep HR between 110-120bpm for 20 mins', 'Continue intensity-focused workouts']
    },
    30: {
      day: 30,
      vivaScore: 80,
      pillars: {
        p1: { score: 85, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 78, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 70, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' },
        sleepDuration: { value: 420, rangeLevel: 'Good' } // 7h
      },
      badges: ['Scan Champion (Tier 4)'],
      points: 100,
      gameUnlocks: ['Tetris'],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "You've stabilized at 80. You are no longer 'recovering', you are building.",
      missions: ['Continue building consistency', 'Maintain 80+ score']
    },
    45: {
      day: 45,
      vivaScore: 81,
      pillars: {
        p1: { score: 85, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 76, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 82, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 79, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 72, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' },
        weight: { value: -0.5, unit: 'kg' }
      },
      badges: ['Metabolic Scout (Tier 1)'],
      points: 75,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Sugar Detective complete! Logging your sugary drinks helped you cut back. Weight is trending down.",
      missions: ['Sugar Detective: Log your sugary drinks for 3 days (keep under 1/day)', 'Continue metabolic focus']
    },
    60: {
      day: 60,
      vivaScore: 82,
      pillars: {
        p1: { score: 88, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 9000, rangeLevel: 'Good' },
        sleepDuration: { value: 420, rangeLevel: 'Good' } // 7h
      },
      badges: ['Metabolic Scout'],
      points: 150,
      heyVivaExpression: 'Proud',
      heyVivaMessage: "The 60-Day Streak Challenge complete! Scan 3 times this week to solidify your 'Elite' status.",
      missions: ['The 60-Day Streak Challenge: Scan 3 times this week', 'Maintain Elite status']
    },
    75: {
      day: 75,
      vivaScore: 82,
      pillars: {
        p1: { score: 88, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 76, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        vo2Max: { value: 38.6, rangeLevel: 'Good' },
        steps: { value: 9000, rangeLevel: 'Good' }
      },
      badges: ['Personal Best: Cardio'],
      points: 200,
      heyVivaExpression: 'Star-Struck',
      heyVivaMessage: "NEW PB! You beat your ghost. VO2Max is 38.6.",
      missions: ['Beat your Personal Best VO2Max (38.5)', 'Continue intensity-focused workouts']
    },
    90: {
      day: 90,
      vivaScore: 83,
      pillars: {
        p1: { score: 88, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 82, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 82, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 78, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        stressLevel: { value: 60, rangeLevel: 'Moderate' },
        steps: { value: 10000, rangeLevel: 'Excellent' }
      },
      points: 250,
      heyVivaExpression: 'Proud',
      heyVivaMessage: "90 days since the 'Burnout'. You haven't just recovered; you're stronger. Your Stress Score is down 20% vs Day 0.",
      missions: ['Quarterly Review: Maintain consistency', 'Continue Personal Best challenges']
    },
    120: {
      day: 120,
      vivaScore: 82,
      pillars: {
        p1: { score: 88, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 85, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 78, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 0, rangeLevel: 'Unknown' }
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "Henry, looks like you're taking a break. Use 500 Points to freeze your Scan Legend streak? ❄️",
      missions: ['Complete a scan when you return', 'Maintain your health routine after vacation']
    },
    135: {
      day: 135,
      vivaScore: 81,
      pillars: {
        p1: { score: 87, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 78, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 83, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 79, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 77, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' },
        sleepDuration: { value: 420, rangeLevel: 'Good' } // 7h
      },
      points: 300,
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "Welcome back! The Re-Calibration: 3 scans in 3 days to verify post-vacation stats.",
      missions: ['The Re-Calibration: 3 scans in 3 days', 'Return to form after vacation']
    },
    160: {
      day: 160,
      vivaScore: 84,
      pillars: {
        p1: { score: 89, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 87, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 82, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 82, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 450, rangeLevel: 'Good' }, // 7h 30m
        steps: { value: 10000, rangeLevel: 'Excellent' }
      },
      badges: ['Sleep Scientist (Tier 4)'],
      points: 350,
      gameUnlocks: ['Chess'],
      prizes: [
        {
          type: 'game',
          name: 'Chess',
          description: 'Strategic game unlocked!',
          icon: 'shield-outline',
          badgeSource: 'Sleep Scientist'
        },
        {
          type: 'reward',
          name: 'Amazon £10 Voucher',
          description: '£10 Amazon gift voucher!',
          icon: 'gift-outline',
          badgeSource: 'Sleep Scientist'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Sleep Scientist complete! You track sleep well, and now you understand cycles. Chess is unlocked as a reward—enjoy quick breaks anytime!",
      missions: ['Sleep Scientist: Pass the Advanced Sleep Quiz', 'Continue knowledge-based challenges']
    },
    200: {
      day: 200,
      vivaScore: 84,
      pillars: {
        p1: { score: 89, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 87, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 82, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 82, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      points: 400,
      heyVivaExpression: 'Coaching',
      heyVivaMessage: "Elite Mission: Keep all 5 Pillars in 'Good' or 'Excellent' for 30 days straight. This is the Perfect Month challenge.",
      missions: ['Perfect Month Challenge: Keep all 5 Pillars in Good or Excellent for 30 days', 'Maintain Elite status']
    },
    215: {
      day: 215,
      vivaScore: 84,
      pillars: {
        p1: { score: 89, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 82, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 87, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 82, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 82, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 420, rangeLevel: 'Good' }, // 7h
        steps: { value: 10000, rangeLevel: 'Excellent' }
      },
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "Early bed tonight saves the Perfect Month. Sleep is dipping slightly—get 7h+ tonight to stay on track.",
      missions: ['Get 7h+ sleep tonight to save Perfect Month', 'Maintain all pillars in Good or Excellent']
    },
    230: {
      day: 230,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 90, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 88, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      badges: ['Perfect Month'],
      points: 450,
      prizes: [
        {
          type: 'reward',
          name: 'Curve Movie Ticket',
          description: 'Free movie ticket from Curve!',
          icon: 'film-outline',
          badgeSource: 'Perfect Month'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Perfect Month challenge complete! You've reached 85 and held it for 30 days.",
      missions: ['Maintain Elite status (85+)', 'Continue Perfect Month consistency']
    },
    250: {
      day: 250,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 90, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 88, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        restingHeartRate: { value: 54, rangeLevel: 'Excellent' },
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      badges: ['Health Master (6 Month Consistency)', 'Perfect Month'],
      points: 500,
      gameUnlocks: ['All Games (Permanent)'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (Permanent)',
          description: 'All arcade games permanently unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Health Master'
        },
        {
          type: 'reward',
          name: 'Amazon £10 Voucher',
          description: '£10 Amazon gift voucher!',
          icon: 'gift-outline',
          badgeSource: 'Health Master'
        },
        {
          type: 'reward',
          name: 'Curve Movie Ticket',
          description: 'Free movie ticket from Curve!',
          icon: 'film-outline',
          badgeSource: 'Health Master'
        },
        {
          type: 'feature',
          name: 'Scan a Friend',
          description: 'Unlock the ability to scan friends and family!',
          icon: 'people-outline',
          badgeSource: 'Health Master'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "You are now a Health Master. Only 2% of users reach this consistency. All games are unlocked as rewards—enjoy quick entertainment breaks anytime!",
      missions: ['Maintain Legend status', 'Continue sustainable lifestyle habits']
    },
    280: {
      day: 280,
      vivaScore: 85,
      pillars: {
        p1: { score: 90, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 88, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 90, status: 'Excellent', name: 'Activity & Movement' },
        p4: { score: 85, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 88, status: 'Excellent', name: 'Mental Wellbeing' }
      },
      observations: {
        restingHeartRate: { value: 54, rangeLevel: 'Excellent' },
        steps: { value: 10000, rangeLevel: 'Excellent' },
        sleepDuration: { value: 450, rangeLevel: 'Good' } // 7h 30m
      },
      badges: ['Health Master (6 Month Consistency)', 'Perfect Month', 'Sleep Scientist (Tier 4)', 'Personal Best: Cardio'],
      points: 500,
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Weekly Report: You maintained Elite status (85). Resting HR is steady at 54. Keep doing what you're doing, Henry.",
      missions: ['Maintain Elite status', 'Continue sustainable lifestyle habits']
    }
  },
  sarah: {
    1: {
      day: 1,
      vivaScore: 58,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 40, status: 'Poor', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 45, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 270, rangeLevel: 'Poor' }, // 4h 30m
        stressLevel: { value: 85, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Concerned',
      heyVivaMessage: "Hi Sarah. A score of 58 means we need to focus on your sleep and stress. You're getting less than 5 hours of sleep, and your stress levels are high. We can fix this together, one night at a time.",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Scout: Get 7+ hours for 3 nights']
    },
    2: {
      day: 2,
      vivaScore: 58,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 42, status: 'Poor', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 45, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 315, rangeLevel: 'Poor' }, // 5h 15m
        stressLevel: { value: 80, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "You're trying, Sarah. Last night was 5h 15m—better than before, but we need 7+ hours. Try going to bed 30 minutes earlier tonight.",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Scout progress: 0/3 nights']
    },
    3: {
      day: 3,
      vivaScore: 58,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 50, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 48, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 435, rangeLevel: 'Good' }, // 7h 15m
        stressLevel: { value: 75, rangeLevel: 'High' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)'],
      points: 15,
      gameUnlocks: ['Pacman'],
      prizes: [
        {
          type: 'game',
          name: 'Pacman',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Sleep Rhythm Scout'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Excellent! 7h 15m of sleep! That's night 1 of 3 for your Sleep Rhythm Scout badge. You're on the right track!",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Scout progress: 1/3 nights']
    },
    4: {
      day: 4,
      vivaScore: 58,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 45, status: 'Poor', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 45, status: 'Poor', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 285, rangeLevel: 'Poor' }, // 4h 45m
        stressLevel: { value: 78, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Supportive',
      heyVivaMessage: "I see work got in the way. That's okay—one night doesn't break your progress. Let's get back on track tonight.",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Scout progress: 0/3 nights (streak reset)']
    },
    5: {
      day: 5,
      vivaScore: 58,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 52, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 50, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 450, rangeLevel: 'Good' }, // 7h 30m
        stressLevel: { value: 72, rangeLevel: 'High' }
      },
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Great recovery! 7h 30m of sleep. That's night 1 again—let's make it 3 in a row this time.",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Scout progress: 1/3 nights']
    },
    7: {
      day: 7,
      vivaScore: 60,
      pillars: {
        p1: { score: 70, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 58, status: 'Fair', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 65, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 52, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 465, rangeLevel: 'Good' }, // 7h 45m
        stressLevel: { value: 70, rangeLevel: 'Moderate' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)'],
      points: 30,
      gameUnlocks: ['Pacman', 'Space Invaders'],
      prizes: [
        {
          type: 'game',
          name: 'Space Invaders',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Sleep Rhythm Explorer'
        }
      ],
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "Sleep Rhythm Explorer badge earned! You've had 7+ hours for 3 nights straight. Your stress is already dropping. Keep this up!",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Explorer: Get 7+ hours for 7 nights']
    },
    14: {
      day: 14,
      vivaScore: 63,
      pillars: {
        p1: { score: 72, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 65, status: 'Average', name: 'Sleep & Recovery' },
        p3: { score: 62, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 66, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 55, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 450, rangeLevel: 'Good' }, // 7h 30m
        stressLevel: { value: 65, rangeLevel: 'Moderate' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)'],
      points: 50,
      gameUnlocks: ['Pacman', 'Space Invaders', 'Snake'],
      prizes: [
        {
          type: 'game',
          name: 'Snake',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Sleep Rhythm Guardian'
        }
      ],
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Sleep Rhythm Guardian! 14 nights of 7+ hours. Your body is thanking you. Your stress has dropped from 85 to 65. This is working!",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Guardian: Get 7+ hours for 14 nights']
    },
    20: {
      day: 20,
      vivaScore: 65,
      pillars: {
        p1: { score: 73, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 68, status: 'Average', name: 'Sleep & Recovery' },
        p3: { score: 63, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 67, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 60, status: 'Fair', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 465, rangeLevel: 'Good' }, // 7h 45m
        stressLevel: { value: 55, rangeLevel: 'Moderate' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)'],
      points: 75,
      gameUnlocks: ['Pacman', 'Space Invaders', 'Snake', 'Sudoku'],
      prizes: [
        {
          type: 'game',
          name: 'Sudoku',
          description: 'Puzzle game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Calm Mind'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Calm Mind badge earned! Your stress has improved from High (85) to Moderate (55). Better sleep is helping you manage stress naturally.",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain low stress levels']
    },
    30: {
      day: 30,
      vivaScore: 68,
      pillars: {
        p1: { score: 75, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 68, status: 'Average', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8h
        stressLevel: { value: 50, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)'],
      points: 100,
      gameUnlocks: ['Pacman', 'Space Invaders', 'Snake', 'Sudoku', 'Chess'],
      prizes: [
        {
          type: 'game',
          name: 'Chess',
          description: 'Strategy game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Sleep Rhythm Champion'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Sleep Rhythm Champion! 30 nights of consistent sleep. You've transformed your sleep habits. Your VivaScore has improved from 58 to 68!",
      missions: ['Get 7+ hours of sleep tonight', 'Sleep Rhythm Champion: Get 7+ hours for 30 nights']
    },
    60: {
      day: 60,
      vivaScore: 70,
      pillars: {
        p1: { score: 78, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 68, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 70, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 495, rangeLevel: 'Excellent' }, // 8h 15m
        stressLevel: { value: 45, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)'],
      points: 150,
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Zero Risk Days badge! All your health metrics are in ideal ranges. You've achieved 7 days of perfect health status.",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain all risk scores in ideal ranges']
    },
    75: {
      day: 75,
      vivaScore: 72,
      pillars: {
        p1: { score: 80, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 82, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 72, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 72, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 525, rangeLevel: 'Excellent' }, // 8h 45m - Personal Best
        stressLevel: { value: 40, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)', 'Personal Best (Sleep Duration)'],
      points: 175,
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "Personal Best! 8h 45m of sleep—your longest night yet! Your body is fully recovering now.",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain excellent sleep patterns']
    },
    90: {
      day: 90,
      vivaScore: 74,
      pillars: {
        p1: { score: 82, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 72, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 74, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8h
        stressLevel: { value: 40, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)', 'Personal Best (Sleep Duration)', 'Sleep Rhythm Legend (Tier 5)'],
      points: 200,
      gameUnlocks: ['Pacman', 'Space Invaders', 'Snake', 'Sudoku', 'Chess', 'All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (48h)',
          description: 'All arcade games unlocked for 48 hours!',
          icon: 'game-controller-outline',
          badgeSource: 'Sleep Rhythm Legend'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Sleep Rhythm Legend! 90 nights of consistent sleep. You've completely transformed your sleep habits. Your stress has dropped from 85 to 40. This is mastery!",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain Sleep Rhythm Legend status']
    },
    120: {
      day: 120,
      vivaScore: 73,
      pillars: {
        p1: { score: 81, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 84, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 71, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 73, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 74, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8h
        stressLevel: { value: 38, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)', 'Personal Best (Sleep Duration)', 'Sleep Rhythm Legend (Tier 5)'],
      points: 220,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "You've maintained excellent sleep for 4 months. Your stress is at an all-time low. You're almost at your target of 75!",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain excellent sleep and stress levels']
    },
    150: {
      day: 150,
      vivaScore: 74,
      pillars: {
        p1: { score: 82, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 72, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 74, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8h
        stressLevel: { value: 38, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)', 'Personal Best (Sleep Duration)', 'Sleep Rhythm Legend (Tier 5)'],
      points: 240,
      heyVivaExpression: 'Encouraging',
      heyVivaMessage: "You're at 74! One more point to reach your target. Keep maintaining your excellent sleep and stress management.",
      missions: ['Get 7+ hours of sleep tonight', 'Maintain excellent sleep and stress levels']
    },
    180: {
      day: 180,
      vivaScore: 75,
      pillars: {
        p1: { score: 83, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 86, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 73, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 75, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 76, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        sleepDuration: { value: 480, rangeLevel: 'Excellent' }, // 8h
        stressLevel: { value: 35, rangeLevel: 'Low' }
      },
      badges: ['Sleep Rhythm Scout (Tier 1)', 'Sleep Rhythm Explorer (Tier 2)', 'Sleep Rhythm Guardian (Tier 3)', 'Calm Mind (Stress Improvement)', 'Sleep Rhythm Champion (Tier 4)', 'Zero Risk Days (7 Days)', 'Personal Best (Sleep Duration)', 'Sleep Rhythm Legend (Tier 5)'],
      points: 250,
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Congratulations, Sarah! You've reached 75! You've transformed from sleepless nights to restful recovery. Your sleep and stress management are now excellent.",
      missions: ['Maintain excellent sleep and stress levels', 'Continue healthy sleep habits']
    }
  },
  marcus: {
    1: {
      day: 1,
      vivaScore: 68,
      pillars: {
        p1: { score: 75, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 6500, rangeLevel: 'Fair' },
        restingHeartRate: { value: 62, rangeLevel: 'Good' }
      },
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Hi Marcus! A score of 68 is a solid start. You're in good shape, but there's room for optimization. Want to learn how to improve? Check out the articles section!",
      missions: ['Read your first article', 'Article Explorer: Read 3 articles to earn a badge']
    },
    2: {
      day: 2,
      vivaScore: 68,
      pillars: {
        p1: { score: 75, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 6500, rangeLevel: 'Fair' }
      },
      points: 10,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Great read! You're learning about your health. That's 1 article down, 2 more for your Article Explorer badge.",
      missions: ['Read another article', 'Article Explorer progress: 1/3 articles']
    },
    5: {
      day: 5,
      vivaScore: 68,
      pillars: {
        p1: { score: 75, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 60, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 6500, rangeLevel: 'Fair' }
      },
      badges: ['Article Explorer (Tier 1)'],
      points: 50,
      gameUnlocks: ['Pacman'],
      prizes: [
        {
          type: 'game',
          name: 'Pacman',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Article Explorer'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Article Explorer badge earned! You've read 3 articles and learned about your health. Knowledge is power!",
      missions: ['Take the Activity & Movement quiz', 'Continue reading articles']
    },
    8: {
      day: 8,
      vivaScore: 68,
      pillars: {
        p1: { score: 75, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 62, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 65, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 6800, rangeLevel: 'Fair' }
      },
      badges: ['Article Explorer (Tier 1)', 'Movement Knowledge Student (Tier 1)'],
      points: 75,
      gameUnlocks: ['Pacman', 'Snake'],
      prizes: [
        {
          type: 'game',
          name: 'Snake',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Movement Knowledge Student'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Movement Knowledge Student! You scored 4/5 on the Activity quiz. You're becoming a health expert!",
      missions: ['Take the Heart Health quiz', 'Continue learning']
    },
    12: {
      day: 12,
      vivaScore: 69,
      pillars: {
        p1: { score: 76, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 72, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 62, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 70, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 66, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7000, rangeLevel: 'Fair' }
      },
      badges: ['Article Explorer (Tier 1)', 'Movement Knowledge Student (Tier 1)', 'Heart Health Student (Tier 1)'],
      points: 105,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders'],
      prizes: [
        {
          type: 'game',
          name: 'Space Invaders',
          description: 'Classic arcade game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Heart Health Student'
        }
      ],
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "Perfect score! Heart Health Student badge earned. You're mastering health knowledge!",
      missions: ['Engage with HeyViva', 'Continue reading articles']
    },
    15: {
      day: 15,
      vivaScore: 69,
      pillars: {
        p1: { score: 76, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 73, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 63, status: 'Fair', name: 'Activity & Movement' },
        p4: { score: 71, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 67, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7200, rangeLevel: 'Fair' }
      },
      badges: ['Article Explorer (Tier 1)', 'Movement Knowledge Student (Tier 1)', 'Heart Health Student (Tier 1)', 'HeyViva Regular (Tier 1)'],
      points: 150,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku'],
      prizes: [
        {
          type: 'game',
          name: 'Sudoku',
          description: 'Puzzle game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'HeyViva Regular'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "HeyViva Regular badge! You've had 5 great conversations with me. I love our chats! Keep asking questions—that's how you learn.",
      missions: ['Read more articles', 'Take more quizzes']
    },
    25: {
      day: 25,
      vivaScore: 70,
      pillars: {
        p1: { score: 77, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 74, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 65, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 72, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 68, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7500, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Movement Knowledge Student (Tier 1)', 'Heart Health Student (Tier 1)', 'HeyViva Regular (Tier 1)'],
      points: 250,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess'],
      prizes: [
        {
          type: 'game',
          name: 'Chess',
          description: 'Strategy game unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Article Explorer Tier 2'
        }
      ],
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Article Explorer Tier 2! You've read 10 articles. Your health knowledge is growing, and so is your score!",
      missions: ['Take the Sleep & Recovery quiz', 'Continue reading articles']
    },
    30: {
      day: 30,
      vivaScore: 71,
      pillars: {
        p1: { score: 78, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 66, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 73, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 69, status: 'Average', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 7800, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Movement Knowledge Student (Tier 1)', 'Heart Health Student (Tier 1)', 'HeyViva Regular (Tier 1)', 'Sleep & Recovery Student (Tier 1)'],
      points: 280,
      heyVivaExpression: 'Happy',
      heyVivaMessage: "Another perfect score! Sleep & Recovery Student badge. You're becoming a health expert across all pillars!",
      missions: ['Continue reading articles', 'Engage with HeyViva regularly']
    },
    40: {
      day: 40,
      vivaScore: 71,
      pillars: {
        p1: { score: 78, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 75, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 67, status: 'Average', name: 'Activity & Movement' },
        p4: { score: 73, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 70, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8000, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Movement Knowledge Student (Tier 1)', 'Heart Health Student (Tier 1)', 'HeyViva Regular (Tier 1)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)'],
      points: 500,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess', 'All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (48h)',
          description: 'All arcade games unlocked for 48 hours!',
          icon: 'game-controller-outline',
          badgeSource: 'Points Collector'
        }
      ],
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Points Collector Tier 1! You've earned 500 points through learning and engagement. Your dedication is paying off!",
      missions: ['Continue learning', 'Take advanced quizzes']
    },
    60: {
      day: 60,
      vivaScore: 73,
      pillars: {
        p1: { score: 80, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 77, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 70, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 75, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 72, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8500, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'HeyViva Regular (Tier 1)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)'],
      points: 750,
      heyVivaExpression: 'Ecstatic',
      heyVivaMessage: "Knowledge Pro badges! You've mastered multiple health topics. Your understanding is translating into better health!",
      missions: ['Continue reading articles', 'Take more advanced quizzes']
    },
    75: {
      day: 75,
      vivaScore: 74,
      pillars: {
        p1: { score: 81, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 78, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 71, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 76, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 73, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 8800, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'HeyViva Regular (Tier 1)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)'],
      points: 900,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess', 'All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (72h)',
          description: 'All arcade games unlocked for 72 hours!',
          icon: 'game-controller-outline',
          badgeSource: 'Article Explorer Tier 3'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Article Explorer Tier 3! 25 articles read. You're a health knowledge expert now!",
      missions: ['Continue reading articles', 'Engage with HeyViva more']
    },
    100: {
      day: 100,
      vivaScore: 76,
      pillars: {
        p1: { score: 83, status: 'Good', name: 'Heart & Blood Pressure' },
        p2: { score: 80, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 74, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 78, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 75, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 9200, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)'],
      points: 1100,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess', 'All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (72h)',
          description: 'All arcade games unlocked for 72 hours!',
          icon: 'game-controller-outline',
          badgeSource: 'HeyViva Regular Tier 2'
        }
      ],
      heyVivaExpression: 'Happy',
      heyVivaMessage: "HeyViva Regular Tier 2! 20 conversations. I love our discussions about health. You're asking great questions!",
      missions: ['Take advanced quizzes', 'Continue learning']
    },
    120: {
      day: 120,
      vivaScore: 78,
      pillars: {
        p1: { score: 85, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 82, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 76, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 80, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 77, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 9500, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)'],
      points: 1400,
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Heart Health Master! You've completed all heart health quizzes with perfect scores. You're a true health expert!",
      missions: ['Continue reading articles', 'Take more master-level quizzes']
    },
    150: {
      day: 150,
      vivaScore: 79,
      pillars: {
        p1: { score: 86, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 83, status: 'Good', name: 'Sleep & Recovery' },
        p3: { score: 77, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 81, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 78, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 9800, rangeLevel: 'Good' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)', 'Points Collector (Tier 2)'],
      points: 2000,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess', 'All Games'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (96h)',
          description: 'All arcade games unlocked for 96 hours!',
          icon: 'game-controller-outline',
          badgeSource: 'Points Collector Tier 2'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Points Collector Tier 2! 2000 points earned through learning and engagement. Your dedication to health education is inspiring!",
      missions: ['Continue reading articles', 'Reach Article Explorer Tier 4']
    },
    180: {
      day: 180,
      vivaScore: 80,
      pillars: {
        p1: { score: 87, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 84, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 78, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 82, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 79, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10000, rangeLevel: 'Excellent' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Article Explorer (Tier 4)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)', 'Points Collector (Tier 2)'],
      points: 2500,
      gameUnlocks: ['Pacman', 'Snake', 'Space Invaders', 'Sudoku', 'Chess', 'All Games (Permanent)'],
      prizes: [
        {
          type: 'game',
          name: 'All Games (Permanent)',
          description: 'All arcade games permanently unlocked!',
          icon: 'game-controller-outline',
          badgeSource: 'Article Explorer Tier 4'
        }
      ],
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Article Explorer Tier 4! 50 articles read. You're a health knowledge master. All games are now permanently unlocked!",
      missions: ['Continue learning', 'Maintain excellent health']
    },
    200: {
      day: 200,
      vivaScore: 81,
      pillars: {
        p1: { score: 88, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 79, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 83, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 80, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10200, rangeLevel: 'Excellent' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Article Explorer (Tier 4)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Points Collector (Tier 1)', 'Points Collector (Tier 2)', 'Points Collector (Tier 3)'],
      points: 3000,
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Points Collector Tier 3! 3000 points. You've earned this through consistent learning and engagement. You're at 81—almost at your target!",
      missions: ['Complete all master-level quizzes', 'Maintain excellent health']
    },
    220: {
      day: 220,
      vivaScore: 81,
      pillars: {
        p1: { score: 88, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 85, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 79, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 83, status: 'Good', name: 'Metabolic & Weight' },
        p5: { score: 80, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10200, rangeLevel: 'Excellent' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Article Explorer (Tier 4)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Sleep & Recovery Master (Tier 3)', 'Points Collector (Tier 1)', 'Points Collector (Tier 2)', 'Points Collector (Tier 3)', 'Health Knowledge Master (All Pillars)'],
      points: 3200,
      heyVivaExpression: 'Legendary',
      heyVivaMessage: "Health Knowledge Master! You've mastered quizzes across all health pillars. You're a true health expert!",
      missions: ['Maintain excellent health', 'Continue learning and engagement']
    },
    240: {
      day: 240,
      vivaScore: 82,
      pillars: {
        p1: { score: 89, status: 'Excellent', name: 'Heart & Blood Pressure' },
        p2: { score: 86, status: 'Excellent', name: 'Sleep & Recovery' },
        p3: { score: 80, status: 'Good', name: 'Activity & Movement' },
        p4: { score: 84, status: 'Excellent', name: 'Metabolic & Weight' },
        p5: { score: 81, status: 'Good', name: 'Mental Wellbeing' }
      },
      observations: {
        steps: { value: 10500, rangeLevel: 'Excellent' },
        restingHeartRate: { value: 58, rangeLevel: 'Excellent' }
      },
      badges: ['Article Explorer (Tier 1)', 'Article Explorer (Tier 2)', 'Article Explorer (Tier 3)', 'Article Explorer (Tier 4)', 'Movement Knowledge Student (Tier 1)', 'Movement Knowledge Pro (Tier 2)', 'Heart Health Student (Tier 1)', 'Heart Health Pro (Tier 2)', 'Heart Health Master (Tier 3)', 'HeyViva Regular (Tier 1)', 'HeyViva Regular (Tier 2)', 'Sleep & Recovery Student (Tier 1)', 'Sleep & Recovery Master (Tier 3)', 'Points Collector (Tier 1)', 'Points Collector (Tier 2)', 'Points Collector (Tier 3)', 'Health Knowledge Master (All Pillars)'],
      points: 3500,
      heyVivaExpression: 'Celebrating',
      heyVivaMessage: "Congratulations, Marcus! You've reached 82! You've transformed from curiosity to health mastery through learning and engagement. Your knowledge has become your health superpower!",
      missions: ['Maintain excellent health', 'Continue learning and sharing knowledge']
    }
  }
};

// Export story data for direct access
export { STORY_DATA };

// Get all key days (days with explicit data, not interpolated) for a story
export function getKeyDaysForStory(storyId: StoryId): number[] {
  const storyData = STORY_DATA[storyId];
  if (!storyData) return [];
  
  return Object.keys(storyData)
    .map(Number)
    .sort((a, b) => a - b);
}

// Check if a day is a key day (has explicit data)
export function isKeyDay(storyId: StoryId, day: number): boolean {
  const storyData = STORY_DATA[storyId];
  if (!storyData) return false;
  return storyData[day] !== undefined;
}

